import { CheckmarkIcon } from '@moia-dev/pace-icons'
import {
  Button,
  Dialog,
  Heading,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Text,
  useDialog,
} from '@moia-dev/pace-web'
import { useEffect, useState } from 'react'
import type { RouteBuilderVtrDataQuery } from './TripEditor.hook'

type VtrData = NonNullable<RouteBuilderVtrDataQuery['vtrData']>
type CbData = VtrData['cbData'][number]

const displayValue = (v: string | null | undefined): string => {
  if (!v || v.trim() === '') return 'N/A'
  return v
}

const GridField = ({
  label,
  value,
}: {
  label: string
  value: string | null | undefined
}) => (
  <div css={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Text size="sm" weight="bold" css={{ display: 'block' }}>
      {label}:
    </Text>
    <Text size="sm" css={{ display: 'block' }}>
      {displayValue(value)}
    </Text>
  </div>
)

const JiraRequirements = ({ vtrData }: { vtrData: VtrData }) => (
  <div
    css={theme => ({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.space.Space3,
    })}
  >
    <div
      css={theme => ({
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: `${theme.space.Space3} ${theme.space.Space4}`,
      })}
    >
      <GridField label="Type" value={vtrData.type} />
      <GridField
        label="Resolution"
        value={vtrData.resolution || 'Unresolved'}
      />
      <GridField label="Priority" value={vtrData.priority} />
      <GridField
        label="Fix Version/s"
        value={vtrData.fixVersion || 'Unresolved'}
      />
      <GridField
        label="Security Level"
        value={vtrData.securityLevel || 'N/A'}
      />
      <GridField label="Feature" value={vtrData.feature || 'Unknown'} />
      <GridField label="Epic Link" value={vtrData.epicLink || 'N/A'} />
      <GridField label="Location(s)" value={vtrData.location} />
      <GridField
        label="Test Specification(s)"
        value={vtrData.testSpecification || 'None'}
      />
      <GridField label="Risk Level" value={vtrData.riskLevel || 'None'} />
      <GridField
        label="Target Compound Release (Asset)"
        value={vtrData.targetCompoundRelease || 'None'}
      />
      <GridField
        label="Requested Software Version/s"
        value={vtrData.requestedSoftwareVersion || 'N/A'}
      />
      <GridField label="Map Version" value={vtrData.mapVersion || 'N/A'} />
      <GridField label="Label" value={vtrData.label || 'N/A'} />
      <GridField
        label="Deviation Permission"
        value={vtrData.deviationPermission || 'Not needed'}
      />
    </div>
    {vtrData.description && (
      <div css={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Text size="sm" weight="bold">
          Description:
        </Text>
        <Text size="sm">{vtrData.description}</Text>
      </div>
    )}
  </div>
)

const CbRequirements = ({
  selectedCbIndex,
  setSelectedCbIndex,
  cbData,
}: {
  selectedCbIndex: number
  setSelectedCbIndex: (i: number) => void
  cbData: CbData[]
}) => {
  const getCbLabel = (entry: CbData, i: number) =>
    entry.type
      ? `${entry.type} - ${entry.codebeamerTestcaseId || i + 1}`
      : entry.codebeamerTestcaseId || `CB ${i + 1}`

  return (
    <div
      css={theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.space.Space3,
      })}
    >
      {cbData.length > 1 && (
        <Tabs defaultId={`cb-0`}>
          <TabList aria-label="CodeBeamer requirements tabs">
            {cbData.map((entry, i) => (
              <span
                key={entry.codebeamerTestcaseId || i}
                onClick={() => setSelectedCbIndex(i)}
                onKeyDown={e => {
                  if (e.key === 'Enter') setSelectedCbIndex(i)
                }}
              >
                <Tab id={`cb-${i}`}>{getCbLabel(entry, i)}</Tab>
              </span>
            ))}
          </TabList>
          {cbData.map((entry, i) => (
            <TabPanel key={entry.codebeamerTestcaseId || i} forId={`cb-${i}`}>
              <CbGrid cb={entry} />
            </TabPanel>
          ))}
        </Tabs>
      )}
      {cbData.length === 1 && (
        <CbGrid cb={cbData[selectedCbIndex] ?? cbData[0]} />
      )}
    </div>
  )
}

const CbGrid = ({ cb }: { cb: CbData }) => (
  <div
    css={theme => ({
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: `${theme.space.Space3} ${theme.space.Space4}`,
      paddingTop: theme.space.Space2,
    })}
  >
    <GridField label="Tracker" value={cb.tracker || 'N/A'} />
    <GridField label="Parent" value={cb.parent || 'N/A'} />
    <GridField label="Status" value={cb.status} />
    <GridField label="Type" value={cb.type} />
    <GridField label="Submitted by" value={cb.submittedBy || 'N/A'} />
    <GridField label="Modified by" value={cb.modifiedBy || 'N/A'} />
    <GridField
      label="Number of unique elements"
      value={cb.numUniqueElements ? String(cb.numUniqueElements) : '0'}
    />
    <GridField label="Location" value={cb.location} />
    <GridField
      label="Target number for each element"
      value={cb.targetNumPerElement ? String(cb.targetNumPerElement) : '0'}
    />
    <GridField label="Target Unit" value={cb.targetUnit || '[n]'} />
    <GridField
      label="Target Constraints"
      value={cb.targetConstraints || 'N/A'}
    />
    <GridField label="CodeBeamer ID" value={cb.codebeamerTestcaseId} />
    {cb.oddElementTypes.length > 0 && (
      <GridField
        label="ODD Element Types"
        value={cb.oddElementTypes.join(', ')}
      />
    )}
    {cb.taxonomies.length > 0 && (
      <GridField label="Taxonomies" value={cb.taxonomies.join(', ')} />
    )}
  </div>
)

type VtrSelectionModalProps = {
  vtrData: VtrData
  onConfirm: (cbIndex: number) => void
  onCancel: () => void
  initialCbIndex?: number
  readOnly?: boolean
}

export const VtrSelectionModal = ({
  vtrData,
  onConfirm,
  onCancel,
  initialCbIndex,
  readOnly = false,
}: VtrSelectionModalProps) => {
  const { id, isOpen, openDialog, closeDialog } = useDialog()
  const hasCb = vtrData.cbData.length > 0
  const [selectedCbIndex, setSelectedCbIndex] = useState(initialCbIndex ?? 0)

  useEffect(() => {
    openDialog()
  }, [openDialog])

  const titleParts = [
    `${vtrData.key} (${vtrData.status})`,
    vtrData.testType,
    [vtrData.description?.split('.')[0], vtrData.location]
      .filter(Boolean)
      .join(' @ '),
  ].filter(Boolean)

  const handleClose = () => {
    closeDialog()
    onCancel()
  }

  return (
    <>
      {isOpen && (
        <Dialog
          id={id}
          title={titleParts.join(' — ')}
          onClose={handleClose}
          css={{ width: '90vw', maxWidth: 1200, maxHeight: '90vh' }}
          actions={
            readOnly ? (
              <Button label="Close" variant="secondary" onClick={handleClose} />
            ) : (
              <>
                <Button
                  label="Dismiss"
                  variant="secondary"
                  onClick={handleClose}
                />
                <Button
                  label="Continue"
                  variant="primary"
                  icon={<CheckmarkIcon />}
                  onClick={() => {
                    closeDialog()
                    onConfirm(hasCb ? selectedCbIndex : 0)
                  }}
                />
              </>
            )
          }
        >
          <div
            css={theme => ({
              overflow: 'auto',
              maxHeight: 'calc(90vh - 160px)',
              padding: `0 ${theme.space.Space4} ${theme.space.Space4}`,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.space.Space4,
            })}
          >
            {/* JIRA Requirements */}
            <div
              css={theme => ({
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space.Space3,
                borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                paddingBottom: theme.space.Space4,
              })}
            >
              <Heading size="sm">JIRA Requirements</Heading>
              <JiraRequirements vtrData={vtrData} />
            </div>

            {/* Code Beamer Requirements */}
            {hasCb && (
              <div
                css={theme => ({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: `calc(${theme.space.Space1} - 5px)`,
                  marginTop: 10,
                })}
              >
                <Heading size="sm">Code Beamer Requirements</Heading>
                <CbRequirements
                  selectedCbIndex={selectedCbIndex}
                  setSelectedCbIndex={setSelectedCbIndex}
                  cbData={vtrData.cbData}
                />
              </div>
            )}
          </div>
        </Dialog>
      )}
    </>
  )
}
